/**
 * Muralys · Voir dans ma pièce
 *
 * Le client importe une photo de sa pièce. Un modele de segmentation (SegFormer, classes ADE20K) tourne DANS son navigateur,
 * repere le mur (en laissant de cote meubles, tableaux, rideaux, luminaires...), puis le decor est pose en perspective (WebGL)
 * sur ce mur, avec les ombres de la photo. Rien n'est envoye, rien n'est conserve.
 * Si le mur n'est pas repere, le client place les quatre coins a la main. Un pinceau permet de corriger.
 */
(() => {
  if (customElements.get('muralys-room-view')) return;

  const MAX_SIDE = 1600;
  const TF_URL = 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.7.2';
  const MODEL = 'Xenova/segformer-b0-finetuned-ade-512-512';
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  const VERT_PHOTO = 'attribute vec2 p; varying vec2 uv; void main(){ gl_Position = vec4(p, 0.0, 1.0); uv = p * 0.5 + 0.5; }';
  const FRAG_PHOTO = 'precision mediump float; uniform sampler2D t; varying vec2 uv; void main(){ gl_FragColor = vec4(texture2D(t, vec2(uv.x, 1.0 - uv.y)).rgb, 1.0); }';
  const VERT_DECOR = 'attribute vec2 p; attribute vec3 t; varying vec3 vt; void main(){ gl_Position = vec4(p, 0.0, 1.0); vt = t; }';
  const FRAG_DECOR = `
    precision mediump float;
    uniform sampler2D dec; uniform sampler2D blur; uniform sampler2D mask; uniform sampler2D wall;
    uniform vec2 res; uniform float avg; uniform vec4 crop;
    varying vec3 vt;
    void main(){
      vec2 uv = vt.xy / vt.z;
      vec2 tc = vec2(crop.x + uv.x * crop.z, crop.y + uv.y * crop.w);
      vec3 col = texture2D(dec, tc).rgb;
      vec2 sc = gl_FragCoord.xy / res; sc.y = 1.0 - sc.y;
      float l = dot(texture2D(blur, sc).rgb, vec3(0.299, 0.587, 0.114));
      float shade = clamp(l / avg, 0.55, 1.3);
      col *= mix(1.0, shade, 0.8);
      float w = texture2D(wall, sc).r;
      float m = texture2D(mask, sc).r;
      gl_FragColor = vec4(col, w * (1.0 - m));
    }`;

  /**
   * Garde-fou : sur certains telephones, l'analyse sature la memoire et le navigateur recharge la page.
   * On note le debut de l'analyse ; si la page revient sans l'avoir terminee, on passe en placement manuel pour cet appareil.
   */
  const FLAG = 'mrv-ai';
  const readFlag = () => { try { return JSON.parse(localStorage.getItem(FLAG) || '{}'); } catch (error) { return {}; } };
  const writeFlag = (value) => { try { localStorage.setItem(FLAG, JSON.stringify(value)); } catch (error) { /* stockage indisponible */ } };
  const aiDisabled = () => {
    const flag = readFlag();
    if (flag.state === 'running' && Date.now() - flag.at < 120000) { writeFlag({ state: 'crashed', at: Date.now() }); return true; }
    return flag.state === 'crashed' && Date.now() - flag.at < 7 * 24 * 3600 * 1000;
  };
  // Une seule recherche a la fois ; le modele est libere apres usage sur les appareils peu dotes en memoire
  const lowMemory = () => !(navigator.deviceMemory >= 4);

  /** Moyenne glissante (fenetre carree de rayon r) par sommes cumulees. */
  const boxMean = (src, w, h, r) => {
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);
    const pre = new Float64Array(Math.max(w, h) + 1);
    for (let y = 0; y < h; y++) {
      pre[0] = 0;
      for (let x = 0; x < w; x++) pre[x + 1] = pre[x] + src[y * w + x];
      for (let x = 0; x < w; x++) {
        const a = Math.max(0, x - r);
        const b = Math.min(w - 1, x + r);
        tmp[y * w + x] = (pre[b + 1] - pre[a]) / (b - a + 1);
      }
    }
    for (let x = 0; x < w; x++) {
      pre[0] = 0;
      for (let y = 0; y < h; y++) pre[y + 1] = pre[y] + tmp[y * w + x];
      for (let y = 0; y < h; y++) {
        const a = Math.max(0, y - r);
        const b = Math.min(h - 1, y + r);
        out[y * w + x] = (pre[b + 1] - pre[a]) / (b - a + 1);
      }
    }
    return out;
  };

  /** Filtre guide (He et al.) : fait epouser au masque p les contours de l'image I. */
  const guidedFilter = (I, p, w, h, r, eps) => {
    const n = w * h;
    const II = new Float32Array(n);
    const Ip = new Float32Array(n);
    for (let i = 0; i < n; i++) { II[i] = I[i] * I[i]; Ip[i] = I[i] * p[i]; }
    const meanI = boxMean(I, w, h, r);
    const meanP = boxMean(p, w, h, r);
    const corrI = boxMean(II, w, h, r);
    const corrIp = boxMean(Ip, w, h, r);
    const a = new Float32Array(n);
    const b = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const varI = corrI[i] - meanI[i] * meanI[i];
      const covIp = corrIp[i] - meanI[i] * meanP[i];
      a[i] = covIp / (varI + eps);
      b[i] = meanP[i] - a[i] * meanI[i];
    }
    const meanA = boxMean(a, w, h, r);
    const meanB = boxMean(b, w, h, r);
    const q = new Float32Array(n);
    for (let i = 0; i < n; i++) q[i] = meanA[i] * I[i] + meanB[i];
    return q;
  };

  let segPromise = null;
  /** Charge le modele une seule fois (mis en cache par le navigateur). */
  const loadSegmenter = (onProgress) => {
    if (!segPromise) {
      segPromise = (async () => {
        const tf = await import(/* @vite-ignore */ TF_URL);
        tf.env.allowLocalModels = false;
        return tf.pipeline('image-segmentation', MODEL, {
          dtype: 'q8',
          device: 'wasm',
          progress_callback: (p) => { if (p.status === 'progress' && p.total) onProgress?.(p); },
        });
      })().catch((error) => { segPromise = null; throw error; });
    }
    return segPromise;
  };

  class MuralysRoomView extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.dialog = this.querySelector('dialog');
      this.loadingText = this.dialog.querySelector('[data-mrv-status]')?.textContent.trim() || '';
      this.$ = (sel) => this.dialog.querySelector(sel);
      this.$$ = (sel) => [...this.dialog.querySelectorAll(sel)];
      this.pts = [{ x: 0.18, y: 0.12 }, { x: 0.82, y: 0.12 }, { x: 0.82, y: 0.78 }, { x: 0.18, y: 0.78 }];
      this.zoom = 1;
      this.tool = 'none';
      this.brush = 34;
      this.split = 62;

      this.querySelector('[data-mrv-open]').addEventListener('click', () => this.open());
      this.$('[data-mrv-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => { this._run = (this._run || 0) + 1; });

      this.$('[data-mrv-file]').addEventListener('change', (e) => this.onFile(e.target.files?.[0]));
      this.$('[data-mrv-cancel]').addEventListener('click', () => { this._run += 1; this._abort?.abort(); this.step('intro'); });
      this.$('[data-mrv-back]').addEventListener('click', () => this.step('intro'));
      this.$('[data-mrv-again]').addEventListener('click', () => this.step('intro'));
      this.$('[data-mrv-apply]').addEventListener('click', () => this.apply());
      this.$('[data-mrv-adjust]').addEventListener('click', () => this.step('place'));
      this.$('[data-mrv-save]').addEventListener('click', () => this.save());
      this.$('[data-mrv-share]').addEventListener('click', () => this.share());
      this.$('[data-mrv-order]').addEventListener('click', () => this.order());
      this.$('[data-mrv-zoom]').addEventListener('input', (e) => { this.zoom = parseFloat(e.target.value); this.render(); });
      this.$('[data-mrv-brush]').addEventListener('input', (e) => { this.brush = parseFloat(e.target.value); });
      for (const radio of this.$$('[data-mrv-tool]')) {
        radio.addEventListener('change', () => {
          this.tool = radio.value;
          this.$('[data-mrv-gl]').classList.toggle('is-brush', this.tool !== 'none');
          if (this.tool !== 'none') this.setSplit(100);
        });
      }
      this.$('[data-mrv-photo]').addEventListener('load', () => this.updatePlace());
      window.addEventListener('resize', () => { if (this.dialog.open) this.updatePlace(); });
      this.bindHandles();
      this.bindBrush();
      this.bindCompare();
      if (navigator.canShare && navigator.share) this.$('[data-mrv-share]').hidden = false;
    }

    open() {
      this.dialog.showModal();
      this.step(this.photo && this.gl ? 'result' : 'intro');
      this.loadDecor();
      // Prechauffe le modele : le resultat arrive plus vite apres l'import
      if (!lowMemory() && !aiDisabled()) loadSegmenter().catch(() => {});
    }

    step(name) {
      for (const section of this.$$('[data-mrv-step]')) section.hidden = section.dataset.mrvStep !== name;
      this.dialog.scrollTop = 0;
      if (name === 'place') this.updatePlace();
      if (name === 'result') { this.layoutResult(); this.render(); }
    }

    loadDecor() {
      if (this.decorImg || this._decorLoading) return;
      this._decorLoading = true;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.decorImg = img; this._decorLoading = false; if (this.gl) this.uploadDecor(); };
      img.onerror = () => { this._decorLoading = false; };
      img.src = this.dataset.src;
    }

    showError(selector, message) {
      const el = this.$(selector);
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* ---------------- Photo ---------------- */

    async onFile(file) {
      this.showError('[data-mrv-error]', '');
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        this.showError('[data-mrv-error]', 'Ce fichier n’est pas une image.');
        return;
      }
      try {
        let bitmap;
        if (window.createImageBitmap) {
          try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
          catch (error) { bitmap = await createImageBitmap(file); }
        }
        let source = bitmap;
        let w;
        let h;
        if (!bitmap) {
          source = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
          });
          w = source.naturalWidth;
          h = source.naturalHeight;
        } else { w = bitmap.width; h = bitmap.height; }
        const k = Math.min(1, MAX_SIDE / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * k);
        canvas.height = Math.round(h * k);
        canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
        if (bitmap?.close) bitmap.close();
        this.photo = canvas;

        if (this._photoUrl) URL.revokeObjectURL(this._photoUrl);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        this._photoUrl = URL.createObjectURL(blob);
        this.$('[data-mrv-photo]').src = this._photoUrl;
        this.$('[data-mrv-before]').src = this._photoUrl;
        this.resetMask();
        this.clearAi();
        this._glPhotoDirty = true;
        this._avgKey = null;
        this.$('[data-mrv-file]').value = '';
        this.analyse();
      } catch (error) {
        console.error(error);
        this.showError('[data-mrv-error]', 'Impossible de lire cette photo. Essayez un fichier JPG ou PNG.');
      }
    }

    /* ---------------- Pose par le service (modele d'edition d'image sur serveur) ---------------- */

    clearAi() {
      if (this.aiUrl) URL.revokeObjectURL(this.aiUrl);
      if (this._beforeUrl) URL.revokeObjectURL(this._beforeUrl);
      this._beforeUrl = null;
      this.aiUrl = null;
      this.$('[data-mrv-compare]').classList.remove('is-ai');
      const after = this.$('[data-mrv-after]');
      after.hidden = true;
      after.removeAttribute('src');
      this.$('[data-mrv-more]').hidden = false;
      this.$('[data-mrv-remaining]').hidden = true;
    }

    jpegB64(source, max) {
      const w = source.naturalWidth || source.width;
      const h = source.naturalHeight || source.height;
      const k = Math.min(1, max / Math.max(w, h));
      const c = document.createElement('canvas');
      c.width = Math.round(w * k);
      c.height = Math.round(h * k);
      c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.9).split(',')[1];
    }

    /** Renvoie true si l'image a ete obtenue et affichee. */
    async generateAi(run) {
      const alive = () => run === this._run;
      this._abort = new AbortController();
      try {
        if (!this.decorImg) await new Promise((resolve) => { const t = setInterval(() => { if (this.decorImg || !alive()) { clearInterval(t); resolve(); } }, 100); setTimeout(resolve, 8000); });
        if (!this.decorImg || !alive()) return false;
        this.setProgress(0.15, this.loadingText);
        // Le modele renvoie un format standard : on recadre la photo (au centre) sur le format le plus proche,
        // pour que « avant » et « apres » se superposent exactement.
        const ratios = [['1:1', 1], ['2:3', 2 / 3], ['3:2', 3 / 2], ['3:4', 3 / 4], ['4:3', 4 / 3], ['4:5', 4 / 5], ['5:4', 5 / 4], ['9:16', 9 / 16], ['16:9', 16 / 9]];
        const own = this.photo.width / this.photo.height;
        const [aspect, target] = ratios.reduce((best, r) => (Math.abs(Math.log(r[1] / own)) < Math.abs(Math.log(best[1] / own)) ? r : best));
        const cw = own > target ? Math.round(this.photo.height * target) : this.photo.width;
        const ch = own > target ? this.photo.height : Math.round(this.photo.width / target);
        const cropped = document.createElement('canvas');
        cropped.width = cw;
        cropped.height = ch;
        cropped.getContext('2d').drawImage(this.photo, Math.round((this.photo.width - cw) / 2), Math.round((this.photo.height - ch) / 2), cw, ch, 0, 0, cw, ch);
        const croppedBlob = await new Promise((resolve) => cropped.toBlob(resolve, 'image/jpeg', 0.92));
        // progression « de confort » pendant l'attente du service
        let p = 0.15;
        const tick = setInterval(() => { p = Math.min(0.9, p + 0.015); this.setProgress(p); }, 700);
        let response;
        try {
          response = await fetch(this.dataset.endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ photo: this.jpegB64(cropped, 1536), decor: this.jpegB64(this.decorImg, 1280), aspect }),
            signal: this._abort.signal,
          });
        } finally { clearInterval(tick); }
        if (!alive()) return true;
        const body = await response.json().catch(() => ({}));
        if (response.status === 429 || body.error === 'limit') {
          this.step('intro');
          this.showError('[data-mrv-error]', this.dataset.errorLimit || 'Vous avez utilisé tous vos essais du jour.');
          return true;
        }
        if (!response.ok || !body.image) return false;

        const bytes = Uint8Array.from(atob(body.image), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: body.mime || 'image/png' });
        this.clearAi();
        this.aiUrl = URL.createObjectURL(blob);
        const probe = new Image();
        await new Promise((resolve, reject) => { probe.onload = resolve; probe.onerror = reject; probe.src = this.aiUrl; });
        if (!alive()) return true;
        const compare = this.$('[data-mrv-compare]');
        compare.classList.add('is-ai');
        compare.style.aspectRatio = `${probe.naturalWidth} / ${probe.naturalHeight}`;
        this._beforeUrl = URL.createObjectURL(croppedBlob);
        this.$('[data-mrv-before]').src = this._beforeUrl;
        const after = this.$('[data-mrv-after]');
        after.src = this.aiUrl;
        after.hidden = false;
        this.$('[data-mrv-more]').hidden = true;
        if (body.remaining !== undefined) {
          const note = this.$('[data-mrv-remaining]');
          note.textContent = (this.dataset.remainingTemplate || '{n} essais restants · {d} essais par jour').replace('{n}', String(body.remaining)).replace('{d}', String(body.perDay ?? ''));
          note.hidden = false;
        }
        this.setProgress(1);
        this.setSplit(62);
        this.$$('[data-mrv-step]').forEach((section) => { section.hidden = section.dataset.mrvStep !== 'result'; });
        return true;
      } catch (error) {
        if (error?.name === 'AbortError') return true;
        console.error('Service de pose indisponible', error);
        return false;
      }
    }

    /* ---------------- Repérage automatique du mur ---------------- */

    setProgress(ratio, text) {
      this.$('[data-mrv-progress]').style.width = `${Math.round(clamp(ratio, 0, 1) * 100)}%`;
      if (text) this.$('[data-mrv-status]').textContent = text;
    }

    async analyse() {
      const run = (this._run = (this._run || 0) + 1);
      const alive = () => run === this._run;
      this.step('loading');
      this.setProgress(0.04, this.loadingText);
      if (this.dataset.endpoint) {
        const done = await this.generateAi(run);
        if (done || !alive()) return;
        // service indisponible : on continue avec l'analyse dans le navigateur
        this.setProgress(0.04, this.loadingText);
      }
      let wallMask = null;
      let floorMask = null;
      const skipAi = aiDisabled();
      try {
        if (skipAi) throw new Error('analyse automatique desactivee sur cet appareil');
        writeFlag({ state: 'running', at: Date.now() });
        const files = {};
        const segmenter = await loadSegmenter((p) => {
          files[p.file] = p;
          const loaded = Object.values(files).reduce((a, f) => a + (f.loaded || 0), 0);
          const total = Object.values(files).reduce((a, f) => a + (f.total || 0), 0);
          if (alive() && total) this.setProgress(0.04 + 0.5 * (loaded / total), 'Préparation de l’analyse (première fois uniquement)…');
        });
        if (!alive()) { writeFlag({ state: 'idle', at: Date.now() }); return; }
        this.setProgress(0.6, 'Repérage du mur…');
        // Image reduite pour l'analyse : moins de memoire, resultat identique (le modele travaille en 512 px)
        const side = 640;
        const k = Math.min(1, side / Math.max(this.photo.width, this.photo.height));
        const tiny = document.createElement('canvas');
        tiny.width = Math.round(this.photo.width * k);
        tiny.height = Math.round(this.photo.height * k);
        tiny.getContext('2d').drawImage(this.photo, 0, 0, tiny.width, tiny.height);
        const tinyBlob = await new Promise((resolve) => tiny.toBlob(resolve, 'image/jpeg', 0.9));
        const tinyUrl = URL.createObjectURL(tinyBlob);
        let result;
        try { result = await segmenter(tinyUrl); } finally { URL.revokeObjectURL(tinyUrl); }
        if (!alive()) { writeFlag({ state: 'idle', at: Date.now() }); return; }
        const wall = result.find((r) => /^wall$/i.test(r.label));
        if (wall) wallMask = wall.mask;
        floorMask = result.find((r) => /^floor$/i.test(r.label))?.mask || null;
        writeFlag({ state: 'ok', at: Date.now() });
        if (lowMemory()) { segPromise = null; segmenter.dispose?.(); }
      } catch (error) {
        if (!skipAi) writeFlag({ state: 'error', at: Date.now() });
        console.error('Repérage du mur impossible', error);
      }
      if (!alive()) { writeFlag({ state: 'idle', at: Date.now() }); return; }
      this.setProgress(0.9);

      const found = wallMask ? this.useWallMask(wallMask, floorMask) : false;
      if (!this.decorImg) await new Promise((resolve) => { const t = setInterval(() => { if (this.decorImg || !alive()) { clearInterval(t); resolve(); } }, 100); setTimeout(resolve, 8000); });
      if (!alive()) return;
      if (!this.initGL()) {
        this.step('intro');
        this.showError('[data-mrv-error]', 'Votre navigateur ne permet pas cet aperçu.');
        return;
      }
      this.setProgress(1);
      if (found) {
        this.setSplit(62);
        this.step('result');
      } else {
        this.wallCanvas = null;
        this.pts = [{ x: 0.18, y: 0.12 }, { x: 0.82, y: 0.12 }, { x: 0.82, y: 0.78 }, { x: 0.18, y: 0.78 }];
        this._wallDirty = true;
        this.$('[data-mrv-place-text]').textContent = 'Le repérage automatique n’est pas disponible pour cette photo. Glissez chaque point sur un angle du mur à habiller, puis posez le décor.';
        this.step('place');
      }
    }

    /** Garde le mur (et ses morceaux separes par des meubles), en deduit les quatre coins et prepare le masque. */
    useWallMask(mask, floorMask) {
      const { width: mw, height: mh, data } = mask;
      const sw = 240;
      const scale = sw / mw;
      const sh = Math.round(mh * scale);
      // reduction du masque (vote majoritaire)
      const small = new Uint8Array(sw * sh);
      for (let y = 0; y < sh; y++) {
        for (let x = 0; x < sw; x++) {
          const sx = Math.min(mw - 1, Math.floor(x / scale));
          const sy = Math.min(mh - 1, Math.floor(y / scale));
          small[y * sw + x] = data[sy * mw + sx] > 127 ? 1 : 0;
        }
      }
      // composantes connexes
      const label = new Int32Array(sw * sh);
      const sizes = [0];
      const stack = [];
      let next = 0;
      for (let i = 0; i < small.length; i++) {
        if (!small[i] || label[i]) continue;
        next += 1;
        let size = 0;
        stack.push(i);
        label[i] = next;
        while (stack.length) {
          const j = stack.pop();
          size += 1;
          const x = j % sw;
          const y = (j / sw) | 0;
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= sw || ny >= sh) continue;
            const n = ny * sw + nx;
            if (small[n] && !label[n]) { label[n] = next; stack.push(n); }
          }
        }
        sizes.push(size);
      }
      const biggest = Math.max(...sizes);
      if (biggest < sw * sh * 0.04) return false;

      // Le mur « central » : la zone de mur la plus proche du centre de la photo, avec les morceaux voisins que les meubles en ont separes.
      let seed = 0;
      for (let radius = 0; radius < Math.max(sw, sh) && !seed; radius += 2) {
        for (let a = 0; a < 360 && !seed; a += 10) {
          const x = Math.round(sw / 2 + Math.cos((a * Math.PI) / 180) * radius);
          const y = Math.round(sh / 2 + Math.sin((a * Math.PI) / 180) * radius);
          if (x >= 0 && y >= 0 && x < sw && y < sh && label[y * sw + x] && sizes[label[y * sw + x]] >= biggest * 0.1) seed = label[y * sw + x];
        }
      }
      if (!seed) seed = sizes.indexOf(biggest);
      const box = sizes.map(() => ({ x0: sw, x1: 0, y0: sh, y1: 0 }));
      for (let i = 0; i < small.length; i++) {
        const l = label[i];
        if (!l) continue;
        const b = box[l];
        const x = i % sw;
        const y = (i / sw) | 0;
        if (x < b.x0) b.x0 = x; if (x > b.x1) b.x1 = x; if (y < b.y0) b.y0 = y; if (y > b.y1) b.y1 = y;
      }
      const sb = box[seed];
      const pad = Math.round(sw * 0.04);
      const keep = sizes.map((size, l) => {
        if (l === 0) return false;
        if (l === seed) return true;
        if (size < sizes[seed] * 0.03) return false;
        const b = box[l];
        // morceau voisin : son centre est dans la zone du mur central (un autre mur, de l'autre cote d'un angle, est ecarte)
        const cx = (b.x0 + b.x1) / 2;
        const cy = (b.y0 + b.y1) / 2;
        return cx > sb.x0 - pad && cx < sb.x1 + pad && cy > sb.y0 - pad && cy < sb.y1 + pad;
      });

      // Geometrie du mur : bords gauche et droit (verticaux), ligne du plafond (haut du mur) et ligne du sol (haut du sol).
      // Les droites sont estimees de facon robuste (mediane des pentes) : le decor suit la perspective sans etre trouble par les meubles.
      const colTop = new Float32Array(sw).fill(-1);
      const colFloor = new Float32Array(sw).fill(-1);
      let xmin = sw;
      let xmax = 0;
      let ymaxWall = 0;
      const cols = new Int32Array(sw);
      for (let i = 0; i < small.length; i++) {
        if (!keep[label[i]]) continue;
        const x = i % sw;
        const y = (i / sw) | 0;
        cols[x] += 1;
        if (colTop[x] < 0 || y < colTop[x]) colTop[x] = y;
        if (y > ymaxWall) ymaxWall = y;
      }
      let total = 0;
      for (let x = 0; x < sw; x++) total += cols[x];
      let acc = 0;
      for (let x = 0; x < sw; x++) { acc += cols[x]; if (acc >= total * 0.005) { xmin = x; break; } }
      acc = 0;
      for (let x = sw - 1; x >= 0; x--) { acc += cols[x]; if (acc >= total * 0.005) { xmax = x; break; } }
      if (xmax - xmin < sw * 0.1) return false;

      if (floorMask) {
        const { width: fw, height: fh, data: fdata } = floorMask;
        for (let y = 0; y < sh; y++) {
          for (let x = 0; x < sw; x++) {
            if (colFloor[x] >= 0) continue;
            const fx = Math.min(fw - 1, Math.floor(x / scale));
            const fy = Math.min(fh - 1, Math.floor(y / scale));
            if (fdata[fy * fw + fx] > 127) colFloor[x] = y;
          }
        }
      }
      const fitLine = (xs, ys) => {
        // Theil-Sen : mediane des pentes, puis mediane des ordonnees a l'origine
        if (xs.length < 8) return null;
        const slopes = [];
        const step = Math.max(1, Math.floor(xs.length / 40));
        for (let a = 0; a < xs.length; a += step) {
          for (let b = a + step; b < xs.length; b += step) {
            if (xs[b] - xs[a] > sw * 0.05) slopes.push((ys[b] - ys[a]) / (xs[b] - xs[a]));
          }
        }
        if (!slopes.length) return null;
        slopes.sort((p, q) => p - q);
        const m = slopes[Math.floor(slopes.length / 2)];
        const inter = ys.map((y, k) => y - m * xs[k]).sort((p, q) => p - q);
        return { m, c: inter[Math.floor(inter.length / 2)] };
      };
      const tx = [];
      const ty = [];
      const fx = [];
      const fy = [];
      for (let x = xmin; x <= xmax; x++) {
        if (colTop[x] >= 0 && cols[x] > sh * 0.04) { tx.push(x); ty.push(colTop[x]); }
        if (colFloor[x] >= 0 && colTop[x] >= 0 && colFloor[x] > colTop[x] + sh * 0.1) { fx.push(x); fy.push(colFloor[x]); }
      }
      const top = fitLine(tx, ty) || { m: 0, c: Math.min(...ty) };
      let bottom = fx.length > (xmax - xmin) * 0.25 ? fitLine(fx, fy) : null;
      // sans sol visible : le bas du mur suit le haut (perspective inconnue), au plus bas du mur repere
      if (!bottom) bottom = { m: top.m, c: ymaxWall + 1 - top.m * ((xmin + xmax) / 2) };
      // le bas du mur ne peut pas depasser le sol visible, ni remonter au-dessus du haut
      const at = (l, x) => l.m * x + l.c;
      let yTL = at(top, xmin);
      let yTR = at(top, xmax);
      let yBL = at(bottom, xmin);
      let yBR = at(bottom, xmax);
      // si le mur monte jusqu'en haut de la photo, le decor part du bord haut
      if (Math.min(...ty) <= 1) { yTL = Math.min(yTL, 0); yTR = Math.min(yTR, 0); }
      // On deborde volontairement du mur : le masque decoupe ensuite le decor, donc rien ne reste sans decor sur les bords.
      const mx = (xmax - xmin) * 0.05;
      const my = ((yBL + yBR) / 2 - (yTL + yTR) / 2) * 0.08;
      const norm = (x, y) => ({ x: clamp(x / sw, -0.3, 1.3), y: clamp(y / sh, -0.3, 1.3) });
      this.pts = [norm(xmin - mx, yTL - my), norm(xmax + mx, yTR - my), norm(xmax + mx, yBR + my), norm(xmin - mx, yBL + my)];
      if (!this.isValidQuad()) return false;

      // Masque du mur, affine sur les contours reels de la photo (filtre guide) : meubles et cadres detoures au plus pres.
      const rw = Math.round(this.photo.width / 2);
      const rh = Math.round(this.photo.height / 2);
      const out = document.createElement('canvas');
      out.width = rw;
      out.height = rh;
      const octx = out.getContext('2d', { willReadFrequently: true });
      const fine = document.createElement('canvas');
      fine.width = mw; fine.height = mh;
      const fctx = fine.getContext('2d');
      const fimg = fctx.createImageData(mw, mh);
      for (let i = 0; i < data.length; i++) {
        const sx = Math.min(sw - 1, Math.floor(((i % mw) * sw) / mw));
        const sy = Math.min(sh - 1, Math.floor((((i / mw) | 0) * sh) / mh));
        const v = data[i] > 127 && keep[label[sy * sw + sx]] ? 255 : 0;
        fimg.data[i * 4] = v; fimg.data[i * 4 + 1] = v; fimg.data[i * 4 + 2] = v; fimg.data[i * 4 + 3] = 255;
      }
      fctx.putImageData(fimg, 0, 0);
      octx.imageSmoothingQuality = 'high';
      octx.drawImage(fine, 0, 0, rw, rh);
      const pdata = octx.getImageData(0, 0, rw, rh).data;
      const prob = new Float32Array(rw * rh);
      for (let i = 0; i < prob.length; i++) prob[i] = pdata[i * 4] / 255;

      const gctx = document.createElement('canvas');
      gctx.width = rw; gctx.height = rh;
      const gc = gctx.getContext('2d', { willReadFrequently: true });
      gc.drawImage(this.photo, 0, 0, rw, rh);
      const gdata = gc.getImageData(0, 0, rw, rh).data;
      const guide = new Float32Array(rw * rh);
      for (let i = 0; i < guide.length; i++) guide[i] = (0.299 * gdata[i * 4] + 0.587 * gdata[i * 4 + 1] + 0.114 * gdata[i * 4 + 2]) / 255;

      const radius = Math.max(4, Math.round(rw * 0.012));
      const refined = guidedFilter(guide, prob, rw, rh, radius, 0.0008);
      const result = octx.createImageData(rw, rh);
      for (let i = 0; i < refined.length; i++) {
        // seuil legerement favorable au decor : pas de liseré de l'ancien mur sur les bords
        const t = clamp((refined[i] - 0.38) / 0.24, 0, 1);
        const a = Math.round(t * t * (3 - 2 * t) * 255);
        result.data[i * 4] = a; result.data[i * 4 + 1] = a; result.data[i * 4 + 2] = a; result.data[i * 4 + 3] = 255;
      }
      octx.putImageData(result, 0, 0);
      this.wallCanvas = out;
      this._wallDirty = true;
      this._avgKey = null;
      return true;
    }

    /* ---------------- Placer / ajuster les coins ---------------- */

    bindHandles() {
      for (const handle of this.$$('[data-mrv-handle]')) {
        const i = parseInt(handle.dataset.mrvHandle, 10);
        const move = (event) => {
          const r = this.$('[data-mrv-photo]').getBoundingClientRect();
          this.pts[i] = { x: clamp((event.clientX - r.left) / r.width, 0, 1), y: clamp((event.clientY - r.top) / r.height, 0, 1) };
          this.updatePlace();
        };
        handle.addEventListener('pointerdown', (event) => {
          handle.setPointerCapture(event.pointerId);
          handle.addEventListener('pointermove', move);
          move(event);
        });
        const end = (event) => {
          handle.removeEventListener('pointermove', move);
          if (handle.hasPointerCapture?.(event.pointerId)) handle.releasePointerCapture(event.pointerId);
        };
        handle.addEventListener('pointerup', end);
        handle.addEventListener('pointercancel', end);
        handle.addEventListener('keydown', (event) => {
          const step = event.shiftKey ? 0.02 : 0.005;
          const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key];
          if (!d) return;
          event.preventDefault();
          this.pts[i] = { x: clamp(this.pts[i].x + d[0], 0, 1), y: clamp(this.pts[i].y + d[1], 0, 1) };
          this.updatePlace();
        });
      }
    }

    updatePlace() {
      const img = this.$('[data-mrv-photo]');
      const stage = this.$('[data-mrv-place-stage]');
      const r = img.getBoundingClientRect();
      const s = stage.getBoundingClientRect();
      const ox = r.left - s.left;
      const oy = r.top - s.top;
      this.$$('[data-mrv-handle]').forEach((handle, i) => {
        handle.style.left = `${ox + clamp(this.pts[i].x, 0, 1) * r.width}px`;
        handle.style.top = `${oy + clamp(this.pts[i].y, 0, 1) * r.height}px`;
      });
      const poly = this.$('[data-mrv-poly]');
      const svg = poly.ownerSVGElement;
      Object.assign(svg.style, { left: `${ox}px`, top: `${oy}px`, width: `${r.width}px`, height: `${r.height}px`, inset: 'auto' });
      poly.setAttribute('points', this.pts.map((p) => `${p.x * 100},${p.y * 100}`).join(' '));
    }

    isValidQuad() {
      const p = this.pts;
      let sign = 0;
      for (let i = 0; i < 4; i++) {
        const a = p[i];
        const b = p[(i + 1) % 4];
        const c = p[(i + 2) % 4];
        const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
        if (Math.abs(cross) < 1e-5) return false;
        const s = Math.sign(cross);
        if (sign && s !== sign) return false;
        sign = s;
      }
      let area = 0;
      for (let i = 0; i < 4; i++) area += p[i].x * p[(i + 1) % 4].y - p[(i + 1) % 4].x * p[i].y;
      return Math.abs(area) / 2 > 0.03;
    }

    apply() {
      if (!this.isValidQuad()) {
        this.showError('[data-mrv-place-error]', 'Les quatre coins doivent former le mur, dans l’ordre : haut gauche, haut droit, bas droit, bas gauche.');
        return;
      }
      this.showError('[data-mrv-place-error]', '');
      if (!this.decorImg) {
        this.showError('[data-mrv-place-error]', 'Le décor se charge, réessayez dans un instant.');
        this.loadDecor();
        return;
      }
      if (!this.initGL()) {
        this.showError('[data-mrv-place-error]', 'Votre navigateur ne permet pas cet aperçu.');
        return;
      }
      this._avgKey = null;
      this.setSplit(62);
      this.step('result');
    }

    /* ---------------- WebGL ---------------- */

    initGL() {
      if (this.gl) { this.uploadDecor(); return true; }
      const canvas = this.$('[data-mrv-gl]');
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true, alpha: false, antialias: true });
      if (!gl) return false;
      this.gl = gl;
      const compile = (type, src) => { const sh = gl.createShader(type); gl.shaderSource(sh, src); gl.compileShader(sh); return sh; };
      const program = (v, f) => {
        const pr = gl.createProgram();
        gl.attachShader(pr, compile(gl.VERTEX_SHADER, v));
        gl.attachShader(pr, compile(gl.FRAGMENT_SHADER, f));
        gl.linkProgram(pr);
        return pr;
      };
      this.progPhoto = program(VERT_PHOTO, FRAG_PHOTO);
      this.progDecor = program(VERT_DECOR, FRAG_DECOR);
      this.bufPhoto = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufPhoto);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      this.bufDecor = gl.createBuffer();
      this.texPhoto = this.makeTex();
      this.texBlur = this.makeTex();
      this.texDecor = this.makeTex();
      this.texMask = this.makeTex();
      this.texWall = this.makeTex();
      this.uploadDecor();
      return true;
    }

    makeTex() {
      const gl = this.gl;
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return tex;
    }

    upload(tex, source) {
      const gl = this.gl;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }

    uploadDecor() { if (this.decorImg && this.gl) this.upload(this.texDecor, this.decorImg); }

    uploadPhoto() {
      const { photo } = this;
      this.upload(this.texPhoto, photo);
      const small = document.createElement('canvas');
      small.width = Math.max(8, Math.round(photo.width / 14));
      small.height = Math.max(8, Math.round(photo.height / 14));
      const ctx = small.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(photo, 0, 0, small.width, small.height);
      this.upload(this.texBlur, small);
      this.small = small;
      this.smallCtx = ctx;
      this._glPhotoDirty = false;
    }

    uploadWall() {
      let source = this.wallCanvas;
      if (!source) {
        source = document.createElement('canvas');
        source.width = 4; source.height = 4;
        const c = source.getContext('2d');
        c.fillStyle = '#fff'; c.fillRect(0, 0, 4, 4);
      }
      this.upload(this.texWall, source);
      this._wallDirty = false;
    }

    /** Luminosite moyenne du mur : sert a reprendre les ombres de la photo sans eclaircir ni assombrir le decor. */
    averageLuma() {
      const { small, smallCtx } = this;
      const data = smallCtx.getImageData(0, 0, small.width, small.height).data;
      let weights = null;
      if (this.wallCanvas) {
        const wc = document.createElement('canvas');
        wc.width = small.width; wc.height = small.height;
        const wctx = wc.getContext('2d', { willReadFrequently: true });
        wctx.drawImage(this.wallCanvas, 0, 0, wc.width, wc.height);
        weights = wctx.getImageData(0, 0, wc.width, wc.height).data;
      }
      const poly = this.pts.map((p) => [p.x * small.width, p.y * small.height]);
      const inside = (x, y) => {
        let c = false;
        for (let i = 0, j = 3; i < 4; j = i++) {
          const [xi, yi] = poly[i];
          const [xj, yj] = poly[j];
          if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
        }
        return c;
      };
      let sum = 0;
      let n = 0;
      for (let y = 0; y < small.height; y++) {
        for (let x = 0; x < small.width; x++) {
          const i = (y * small.width + x) * 4;
          const wgt = weights ? weights[i] / 255 : inside(x + 0.5, y + 0.5) ? 1 : 0;
          if (wgt < 0.5) continue;
          sum += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
          n++;
        }
      }
      return n ? Math.max(0.2, sum / n) : 0.6;
    }

    quadWeights() {
      const [a, b, c, d] = this.pts;
      const r = { x: c.x - a.x, y: c.y - a.y };
      const s = { x: d.x - b.x, y: d.y - b.y };
      const q = { x: b.x - a.x, y: b.y - a.y };
      const denom = r.x * s.y - r.y * s.x;
      if (Math.abs(denom) < 1e-9) return [1, 1, 1, 1];
      const t = (q.x * s.y - q.y * s.x) / denom;
      const u = (q.x * r.y - q.y * r.x) / denom;
      const safe = (v) => clamp(v, 0.02, 0.98);
      return [1 / (1 - safe(t)), 1 / (1 - safe(u)), 1 / safe(t), 1 / safe(u)];
    }

    layoutResult() {
      if (!this.photo || this.aiUrl) return;
      this.$('[data-mrv-compare]').style.aspectRatio = `${this.photo.width} / ${this.photo.height}`;
    }

    render() {
      const gl = this.gl;
      if (!gl || !this.photo || !this.decorImg) return;
      const canvas = this.$('[data-mrv-gl]');
      if (canvas.width !== this.photo.width || canvas.height !== this.photo.height) {
        canvas.width = this.photo.width;
        canvas.height = this.photo.height;
      }
      if (this._glPhotoDirty) this.uploadPhoto();
      if (this._maskDirty && this.mask) { this.upload(this.texMask, this.mask); this._maskDirty = false; }
      if (this._wallDirty) this.uploadWall();
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.disable(gl.BLEND);

      gl.useProgram(this.progPhoto);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufPhoto);
      const pLoc = gl.getAttribLocation(this.progPhoto, 'p');
      gl.enableVertexAttribArray(pLoc);
      gl.vertexAttribPointer(pLoc, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.texPhoto);
      gl.uniform1i(gl.getUniformLocation(this.progPhoto, 't'), 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      const w = this.quadWeights();
      const P = this.pts.map((p) => [p.x * 2 - 1, 1 - p.y * 2]);
      const uvs = [[0, 0], [1, 0], [1, 1], [0, 1]];
      const verts = [];
      for (const i of [0, 1, 2, 0, 2, 3]) verts.push(P[i][0], P[i][1], uvs[i][0] * w[i], uvs[i][1] * w[i], w[i]);
      gl.useProgram(this.progDecor);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufDecor);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.DYNAMIC_DRAW);
      const loc = (n) => gl.getAttribLocation(this.progDecor, n);
      gl.enableVertexAttribArray(loc('p'));
      gl.vertexAttribPointer(loc('p'), 2, gl.FLOAT, false, 20, 0);
      gl.enableVertexAttribArray(loc('t'));
      gl.vertexAttribPointer(loc('t'), 3, gl.FLOAT, false, 20, 8);

      const dist = (a, b) => Math.hypot((a.x - b.x) * canvas.width, (a.y - b.y) * canvas.height);
      const Wd = (dist(this.pts[0], this.pts[1]) + dist(this.pts[3], this.pts[2])) / 2;
      const Hd = (dist(this.pts[0], this.pts[3]) + dist(this.pts[1], this.pts[2])) / 2;
      const A = Wd / Math.max(1, Hd);
      const I = this.decorImg.naturalWidth / this.decorImg.naturalHeight;
      let uw;
      let vh;
      if (A >= I) { uw = 1 / this.zoom; vh = I / A / this.zoom; } else { vh = 1 / this.zoom; uw = A / I / this.zoom; }
      gl.uniform4f(gl.getUniformLocation(this.progDecor, 'crop'), (1 - uw) / 2, (1 - vh) / 2, uw, vh);
      gl.uniform2f(gl.getUniformLocation(this.progDecor, 'res'), canvas.width, canvas.height);
      const key = JSON.stringify(this.pts);
      if (this._avgKey !== key) { this._avg = this.averageLuma(); this._avgKey = key; }
      gl.uniform1f(gl.getUniformLocation(this.progDecor, 'avg'), this._avg);

      const bind = (unit, tex, name) => {
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1i(gl.getUniformLocation(this.progDecor, name), unit);
      };
      bind(0, this.texDecor, 'dec');
      bind(1, this.texBlur, 'blur');
      bind(2, this.texMask, 'mask');
      bind(3, this.texWall, 'wall');
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.activeTexture(gl.TEXTURE0);
    }

    /* ---------------- Avant / après ---------------- */

    setSplit(value) {
      this.split = clamp(value, 0, 100);
      const compare = this.$('[data-mrv-compare]');
      compare.style.setProperty('--mrv-split', `${this.split}%`);
      this.$('[data-mrv-divider]').setAttribute('aria-valuenow', String(Math.round(this.split)));
    }

    bindCompare() {
      const compare = this.$('[data-mrv-compare]');
      const divider = this.$('[data-mrv-divider]');
      this.setSplit(62);
      let drag = false;
      const move = (event) => {
        const r = compare.getBoundingClientRect();
        this.setSplit(((event.clientX - r.left) / r.width) * 100);
      };
      divider.addEventListener('pointerdown', (event) => { drag = true; divider.setPointerCapture(event.pointerId); move(event); });
      divider.addEventListener('pointermove', (event) => { if (drag) move(event); });
      const end = () => { drag = false; };
      divider.addEventListener('pointerup', end);
      divider.addEventListener('pointercancel', end);
      compare.addEventListener('pointerdown', (event) => { if (this.tool === 'none' && event.target !== divider && !divider.contains(event.target)) { drag = true; divider.setPointerCapture?.(event.pointerId); move(event); } });
      divider.addEventListener('keydown', (event) => {
        const step = event.shiftKey ? 10 : 3;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.setSplit(this.split - step); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.setSplit(this.split + step); }
      });
    }

    /* ---------------- Pinceau ---------------- */

    resetMask() {
      const w = Math.round(this.photo.width / 2);
      const h = Math.round(this.photo.height / 2);
      this.mask = document.createElement('canvas');
      this.mask.width = w;
      this.mask.height = h;
      this.maskCtx = this.mask.getContext('2d');
      this.maskCtx.fillStyle = '#000';
      this.maskCtx.fillRect(0, 0, w, h);
      this._maskDirty = true;
    }

    bindBrush() {
      const canvas = this.$('[data-mrv-gl]');
      let last = null;
      const point = (event) => {
        const r = canvas.getBoundingClientRect();
        return { x: ((event.clientX - r.left) / r.width) * this.mask.width, y: ((event.clientY - r.top) / r.height) * this.mask.height, scale: this.mask.width / r.width };
      };
      const stamp = (p) => {
        const ctx = this.maskCtx;
        const radius = Math.max(2, this.brush * p.scale * 0.5);
        const g = ctx.createRadialGradient(p.x, p.y, radius * 0.4, p.x, p.y, radius);
        // « gommer » = laisser voir la photo ; « remettre » = reposer le decor
        const color = this.tool === 'erase' ? '255,255,255' : '0,0,0';
        g.addColorStop(0, `rgba(${color},1)`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
      };
      const paint = (event) => {
        const p = point(event);
        if (last) {
          const dist = Math.hypot(p.x - last.x, p.y - last.y);
          const steps = Math.max(1, Math.ceil(dist / (this.brush * p.scale * 0.2)));
          for (let i = 1; i <= steps; i++) stamp({ x: last.x + ((p.x - last.x) * i) / steps, y: last.y + ((p.y - last.y) * i) / steps, scale: p.scale });
        } else stamp(p);
        last = p;
        this.upload(this.texMask, this.mask);
        this.render();
      };
      canvas.addEventListener('pointerdown', (event) => {
        if (this.tool === 'none') return;
        event.stopPropagation();
        canvas.setPointerCapture(event.pointerId);
        last = null;
        paint(event);
      });
      canvas.addEventListener('pointermove', (event) => { if (this.tool !== 'none' && canvas.hasPointerCapture(event.pointerId)) paint(event); });
      const end = () => { last = null; };
      canvas.addEventListener('pointerup', end);
      canvas.addEventListener('pointercancel', end);
    }

    /* ---------------- Export ---------------- */

    async blob() {
      if (this.aiUrl) return fetch(this.aiUrl).then((r) => r.blob());
      this.render();
      const src = this.$('[data-mrv-gl]');
      const out = document.createElement('canvas');
      out.width = src.width;
      out.height = src.height;
      const ctx = out.getContext('2d');
      ctx.drawImage(src, 0, 0);
      const size = Math.max(14, Math.round(out.width / 70));
      ctx.font = `500 ${size}px Jost, Helvetica, Arial, sans-serif`;
      const text = `MURALYS · ${this.dataset.name}`.toUpperCase();
      const pad = size * 0.8;
      const tw = ctx.measureText(text).width;
      ctx.fillStyle = 'rgba(36,48,64,0.82)';
      ctx.fillRect(0, out.height - size * 2.4, tw + pad * 2, size * 2.4);
      ctx.fillStyle = '#FAF8F3';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, pad, out.height - size * 1.2);
      return new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.92));
    }

    async save() {
      const blob = await this.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `muralys-${this.dataset.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-chez-moi.jpg`;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    }

    async share() {
      const blob = await this.blob();
      const file = new File([blob], 'muralys-chez-moi.jpg', { type: 'image/jpeg' });
      try {
        if (navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: `Muralys · ${this.dataset.name}` });
      } catch (error) { /* partage annule */ }
    }

    order() {
      this.dialog.close();
      const target = document.querySelector('muralys-configurator') || document.querySelector('[data-mc-cta]');
      target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  customElements.define('muralys-room-view', MuralysRoomView);
})();
